<script setup lang="ts">
import { computed } from "vue"
import { cva } from "class-variance-authority"
import { XMarkIcon } from "@heroicons/vue/24/solid"
import { TransitionRoot, TransitionChild, Dialog, DialogPanel } from "@headlessui/vue"

interface Props {
  modelValue: boolean
  /** Renders a bordered header bar with this title. Omit for the plain NIR look. */
  title?: string
  /** Legacy string size API; the boolean size props below take precedence. */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'xxl' | 'full'
  bgClass?: string
  pdfPreview?: boolean
  /** When true, outside-click/Esc do not close the modal. */
  static?: boolean
  noCloseOnBackdrop?: boolean
  hideHeader?: boolean
  hideHeaderClose?: boolean
  /** Legacy: the footer now only renders when a #footer slot is passed. */
  hideFooter?: boolean
  xs?: boolean
  sm?: boolean
  md?: boolean
  lg?: boolean
  xl?: boolean
  xl2?: boolean
  xl4?: boolean
  xl6?: boolean
  xl8?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  bgClass: 'bg-white',
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  close: []
}>()

const legacySizeClasses: Record<string, string> = {
  sm: '!max-w-md',
  md: '!max-w-lg',
  lg: '!max-w-2xl',
  xl: '!max-w-4xl',
  '2xl': '!max-w-6xl',
  xxl: '!max-w-6xl',
  full: '!max-w-7xl',
}

const modalClass = computed(() => {
  const base = cva(`w-full transform rounded-md z-[65] ${props.bgClass} text-left align-middle shadow-xl transition-all`, {
    variants: {
      xs: { true: "!max-w-xs" },
      sm: { true: "!max-w-sm" },
      md: { true: "!max-w-md" },
      lg: { true: "!max-w-lg" },
      xl: { true: "!max-w-xl" },
      xl2: { true: "!max-w-2xl" },
      xl4: { true: "!max-w-4xl" },
      xl6: { true: "!max-w-6xl" },
      xl8: { true: "!max-w-8xl" },
    }
  })({
    xs: props.xs,
    sm: props.sm,
    md: props.md,
    lg: props.lg,
    xl: props.xl,
    xl2: props.xl2,
    xl4: props.xl4,
    xl6: props.xl6,
    xl8: props.xl8,
  })

  const hasBooleanSize = props.xs || props.sm || props.md || props.lg || props.xl
    || props.xl2 || props.xl4 || props.xl6 || props.xl8
  if (hasBooleanSize) return base
  return `${base} ${legacySizeClasses[props.size ?? 'md'] ?? legacySizeClasses.md}`
})

const showHeader = computed(() => props.title !== '' && !props.hideHeader)

const closeModal = () => {
  if (props.static || props.noCloseOnBackdrop) {
    emit('update:modelValue', true)
    return
  }
  emit('update:modelValue', false)
  emit('close')
}

const forceClose = () => {
  emit('update:modelValue', false)
  emit('close')
}
</script>

<template>
  <TransitionRoot appear :show="modelValue" as="template">
    <Dialog as="div" @close="closeModal" class="relative">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-opacity-70 backdrop-blur-sm z-[60]" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto z-[61]">
        <div class="flex min-h-full items-center justify-center text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel :class="modalClass">
              <!-- Titled header -->
              <div v-if="showHeader" class="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 class="text-lg font-semibold text-gray-900">{{ title }}</h3>
                <button
                  v-if="!hideHeaderClose"
                  type="button"
                  @click="forceClose"
                  class="p-1 bg-neutral-300 hover:bg-danger group rounded-md cursor-pointer flex items-center"
                >
                  <XMarkIcon class="w-5 h-5 text-neutral-800 group-hover:text-white" aria-hidden="true" />
                </button>
              </div>

              <!-- Floating close button (no header) -->
              <button
                v-else-if="!hideHeaderClose"
                type="button"
                @click="forceClose"
                class="absolute z-[75] p-1 bg-neutral-300 hover:bg-danger group right-1.5 top-1.5 rounded-md cursor-pointer flex items-center"
              >
                <XMarkIcon class="w-5 h-5 text-neutral-800 group-hover:text-white" aria-hidden="true" />
              </button>

              <div class="p-4 relative">
                <div v-if="pdfPreview" oncontextmenu="return false;" class="fixed inset-0 z-[70]"></div>
                <slot />
              </div>

              <div v-if="$slots.footer" class="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
                <slot name="footer" />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
